import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCustomerAuth } from "../AuthContext";
import { Loader2, Mail, Lock, User, Phone } from "lucide-react";
import { toast } from "sonner";

export default function SignUp() {
  const { register } = useCustomerAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [adultConfirmed, setAdultConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const canSubmit = adultConfirmed && termsAccepted && !busy;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (!adultConfirmed) {
      toast.error("You must confirm you are 18 years or older to register.");
      return;
    }
    if (!termsAccepted) {
      toast.error("Please accept the Terms and Privacy Policy.");
      return;
    }
    setBusy(true);
    try {
      await register({ ...form, adult_confirmed: true });
      toast.success("Account created");
      navigate("/profile", { replace: true });
    } catch (err) {
      toast.error("Sign-up failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="card-soft p-8">
        <h1 className="text-2xl font-extrabold tracking-tight mb-1">Create your account</h1>
        <p className="text-gray-500 mb-6 text-sm">Save your details and book faster next time.</p>

        <form onSubmit={onSubmit} className="space-y-3" data-testid="signup-form">
          <div className="relative">
            <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="input-soft pl-9"
              placeholder="Full name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              required
              data-testid="signup-name"
            />
          </div>
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="email"
              className="input-soft pl-9"
              placeholder="Email"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              required
              data-testid="signup-email"
            />
          </div>
          <div className="relative">
            <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              className="input-soft pl-9"
              placeholder="Phone number"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              data-testid="signup-phone"
            />
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="password"
              className="input-soft pl-9"
              placeholder="Password (6+ chars)"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              required
              minLength={6}
              data-testid="signup-password"
            />
          </div>
          <div className="space-y-2.5 pt-1">
            <label className="flex items-start gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                className="mt-0.5 w-4 h-4 accent-red-600 cursor-pointer"
                checked={adultConfirmed}
                onChange={(e) => setAdultConfirmed(e.target.checked)}
                data-testid="signup-adult-confirm"
              />
              <span className="text-xs text-gray-700 leading-snug">
                I confirm that I am <strong>18 years or older</strong> and legally able to enter a rental agreement.
              </span>
            </label>
            <label className="flex items-start gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                className="mt-0.5 w-4 h-4 accent-red-600 cursor-pointer"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                data-testid="signup-terms-accept"
              />
              <span className="text-xs text-gray-700 leading-snug">
                I agree to the{" "}
                <a
                  href="https://rental-routes.emergent.host/api/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-600 hover:text-red-700 font-semibold"
                >
                  Terms
                </a>{" "}
                and{" "}
                <a
                  href="https://rental-routes.emergent.host/api/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-600 hover:text-red-700 font-semibold"
                >
                  Privacy Policy
                </a>
                .
              </span>
            </label>
          </div>
          <button
            type="submit"
            disabled={!canSubmit}
            className="pill-btn primary w-full"
            style={{
              padding: "0.75rem",
              opacity: canSubmit ? 1 : 0.55,
              cursor: canSubmit ? "pointer" : "not-allowed",
            }}
            data-testid="signup-submit"
            title={!canSubmit && !busy ? "Confirm details above to continue" : undefined}
          >
            {busy ? <Loader2 className="animate-spin inline mr-1" size={14} /> : null}
            {busy
              ? "Creating account..."
              : canSubmit
              ? "Create account"
              : "Confirm details above to continue"}
          </button>
        </form>

        <p className="text-sm text-gray-600 text-center mt-5">
          Already have an account?{" "}
          <Link to="/sign-in" className="text-red-600 font-semibold hover:text-red-700">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
